import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ProductCard from "./ProductCard";
import { refurbishedProducts } from "../../assets/refurbishedProducts";

const PRODUCTS_PER_PAGE = 9;

const NewProducts = () => {
  const [activeCategory, setActiveCategory] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("default");

  const categories = [
    "ALL",
    ...new Set(
      refurbishedProducts.map((product) => product.category || "Uncategorized"),
    ),
  ];

  const filteredProducts = refurbishedProducts.filter((product) => {
    let matchesCategory = true;
    if (activeCategory !== "ALL") {
      matchesCategory = (product.category || "Uncategorized") === activeCategory;
    }
    const matchesSearch =
      searchTerm === "" ||
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === "price-low") return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
    if (sortBy === "price-high") return (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0);
    if (sortBy === "name-asc") return (a.name || "").localeCompare(b.name || "");
    if (sortBy === "name-desc") return (b.name || "").localeCompare(a.name || "");
    return 0;
  });

  const totalPages = Math.ceil(sortedProducts.length / PRODUCTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const endIndex = startIndex + PRODUCTS_PER_PAGE;
  const currentProducts = sortedProducts.slice(startIndex, endIndex);

  useEffect(() => { setCurrentPage(1); }, [activeCategory, searchTerm, sortBy]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setIsFilterOpen(false);
  };

  const clearFilters = () => {
    setActiveCategory("ALL");
    setSearchTerm("");
    setSortBy("default");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    if (totalPages === 0) return pages;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const getDisplayRange = () => {
    if (sortedProducts.length === 0) return "0";
    return `${startIndex + 1}–${Math.min(endIndex, sortedProducts.length)}`;
  };

  return (
    <TheNewProducts>
      <div className="container">

        <div className="section-header">
          <span className="section-tag">Inventory</span>
          <h2>Refurbished Medical Equipment</h2>
          {/* <p className="section-subtitle">
            Certified pre-owned medical devices tested to Medichy quality standards
          </p> */}
        </div>

        {/* ── FILTER BAR ── */}
        <div className="filter-bar">
          <div className="filter-top-row">
            {/* Search */}
            <div className="search-wrapper">
              <svg className="search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search by name or SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button className="clear-search" onClick={() => setSearchTerm("")}>&#10005;</button>
              )}
            </div>

            {/* Sort */}
            <select className="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="default">Sort: Default</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name-asc">Name: A to Z</option>
              <option value="name-desc">Name: Z to A</option>
            </select>
          </div>

          {/* Desktop category tabs */}
          <div className="category-tabs desktop-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`cat-tab ${activeCategory === cat ? "active" : ""}`}
                onClick={() => handleCategoryChange(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Mobile category toggle */}
          <div className="mobile-filter-toggle">
            <button
              className="filter-toggle-btn"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <span className="toggle-label">Category</span>
              <span className="toggle-value">{activeCategory}</span>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                <path d={isFilterOpen ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"} />
              </svg>
            </button>

            {isFilterOpen && (
              <div className="mobile-filters">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    className={`mob-cat-btn ${activeCategory === cat ? "active" : ""}`}
                    onClick={() => handleCategoryChange(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── RESULTS BAR ── */}
        <div className="results-bar">
          <span className="results-text">
            Showing <strong>{getDisplayRange()}</strong> of <strong>{sortedProducts.length}</strong> products
            {activeCategory !== "ALL" && <> in <strong>{activeCategory}</strong></>}
            {searchTerm && <> matching <strong>"{searchTerm}"</strong></>}
          </span>
          {(activeCategory !== "ALL" || searchTerm || sortBy !== "default") && (
            <button className="clear-btn" onClick={clearFilters}>Clear filters</button>
          )}
        </div>

        {/* ── GRID ── */}
        {currentProducts.length > 0 ? (
          <>
            <div className="product-grid">
              {currentProducts.map((product, index) => (
                <ProductCard key={product.id || index} product={product} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pg-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &#8592; Prev
                </button>

                <div className="page-numbers">
                  {getPageNumbers().map((page, index) => (
                    <button
                      key={index}
                      className={`pg-num ${currentPage === page ? "active" : ""} ${page === "..." ? "dots" : ""}`}
                      onClick={() => page !== "..." && handlePageChange(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="pg-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next &#8594;
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="no-results">
            <span className="no-results-tag">No results</span>
            <h3>No products found</h3>
            <p>Try adjusting your search or filter criteria.</p>
            <button className="reset-btn" onClick={clearFilters}>View all products</button>
          </div>
        )}

      </div>
    </TheNewProducts>
  );
};

const TheNewProducts = styled.div`
  width: 100%;
  background: #f5f2ed;
  padding: 5rem 0 6rem;
  /* font-family: 'Georgia', 'Times New Roman', serif; */

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 4rem;
  }

  /* ── HEADER ── */
  .section-header {
    margin-bottom: 3rem;

    .section-tag {
      display: inline-block;
      /* font-family: 'Courier New', monospace; */
      font-size: 0.7rem;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: #7a7265;
      margin-bottom: 0.8rem;
    }

    h2 {
      font-size: clamp(1.6rem, 3vw, 2.4rem);
      font-weight: 400;
      color: #1c1c1c;
      margin: 0 0 0.8rem;
      line-height: 1.2;
    }

    .section-subtitle {
      font-size: 0.95rem;
      color: #7a7265;
      font-style: italic;
      margin: 0;
      line-height: 1.6;
    }
  }

  /* ── FILTER BAR ── */
  .filter-bar {
    margin-bottom: 2rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .filter-top-row {
      display: flex;
      gap: 1rem;
      align-items: center;

      .search-wrapper {
        flex: 1;
        position: relative;

        .search-icon {
          position: absolute;
          left: 0.9rem;
          top: 50%;
          transform: translateY(-50%);
          color: #b0a898;
          pointer-events: none;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 2.2rem 0.75rem 2.4rem;
          border: 1px solid #d5d0c8;
          border-radius: 0;
          background: #fff;
          font-size: 0.9rem;
          font-family: "Georgia", serif;
          color: #1c1c1c;
          transition: border-color 0.2s;

          &::placeholder {
            color: #b0a898;
          }
          &:focus {
            outline: none;
            border-color: #1c1c1c;
          }
        }

        .clear-search {
          position: absolute;
          right: 0.8rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          font-size: 0.75rem;
          color: #b0a898;
          cursor: pointer;
          padding: 0;
          transition: color 0.2s;

          &:hover {
            color: #1c1c1c;
          }
        }
      }

      .sort-select {
        padding: 0.75rem 1rem;
        border: 1px solid #d5d0c8;
        border-radius: 0;
        background: #fff;
        font-size: 0.8rem;
        font-family: "Courier New", monospace;
        letter-spacing: 0.06em;
        color: #1c1c1c;
        cursor: pointer;
        min-width: 200px;
        transition: border-color 0.2s;

        &:focus {
          outline: none;
          border-color: #1c1c1c;
        }
      }
    }

    /* Desktop category tabs */
    .category-tabs {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      border-top: 1px solid #d5d0c8;
      padding-top: 1rem;

      .cat-tab {
        padding: 0.35rem 1rem;
        background: transparent;
        border: 1px solid #d5d0c8;
        border-radius: 100px;
        font-family: "Courier New", monospace;
        font-size: 0.68rem;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: #7a7265;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          border-color: #1c1c1c;
          color: #1c1c1c;
        }

        &.active {
          background: #1c1c1c;
          border-color: #1c1c1c;
          color: #f5f2ed;
        }
      }
    }

    .mobile-filter-toggle {
      display: none;
      position: relative;

      .filter-toggle-btn {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        background: #fff;
        border: 1px solid #d5d0c8;
        font-family: "Courier New", monospace;
        font-size: 0.72rem;
        letter-spacing: 0.1em;
        color: #1c1c1c;
        cursor: pointer;
        text-transform: uppercase;

        .toggle-label {
          color: #7a7265;
        }
        .toggle-value {
          flex: 1;
          text-align: left;
        }
      }

      .mobile-filters {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: #fff;
        border: 1px solid #d5d0c8;
        border-top: none;
        z-index: 100;
        display: flex;
        flex-direction: column;

        .mob-cat-btn {
          padding: 0.8rem 1rem;
          text-align: left;
          background: none;
          border: none;
          border-bottom: 1px solid #ede8e0;
          font-family: "Courier New", monospace;
          font-size: 0.72rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #555;
          cursor: pointer;
          transition: background 0.15s;

          &:last-child {
            border-bottom: none;
          }
          &:hover {
            background: #f5f2ed;
          }
          &.active {
            background: #1c1c1c;
            color: #f5f2ed;
          }
        }
      }
    }
  }

  /* ── RESULTS BAR ── */
  .results-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-bottom: 1.2rem;
    border-bottom: 1px solid #d5d0c8;
    margin-bottom: 2.5rem;

    .results-text {
      font-family: "Courier New", monospace;
      font-size: 0.72rem;
      letter-spacing: 0.08em;
      color: #7a7265;
    }

    .clear-btn {
      background: none;
      border: none;
      font-family: "Courier New", monospace;
      font-size: 0.68rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: #7a7265;
      cursor: pointer;
      text-decoration: underline;
      text-underline-offset: 3px;
      padding: 0;
      transition: color 0.2s;

      &:hover {
        color: #1c1c1c;
      }
    }
  }

  /* ── PRODUCT GRID ── */
  .product-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    margin-bottom: 3rem;
  }

  /* ── PAGINATION ── */
  .pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--primary-color);

    .pg-btn {
      background: transparent;
      border: 1px solid var(--primary-color);
      padding: 0.5rem 1.2rem;
      font-family: "Courier New", monospace;
      font-size: 0.68rem;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--primary-color);
      cursor: pointer;
      transition: all 0.2s;

      &:hover:not(:disabled) {
        border-color: 1px solid var(--primary-color);
        color: #1c1c1c;
      }
      &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }
    }

    .page-numbers {
      display: flex;
      align-items: center;
      gap: 0.35rem;

      .pg-num {
        min-width: 34px;
        height: 34px;
        background: transparent;
        border: 1px solid #d5d0c8;
        font-family: "Courier New", monospace;
        font-size: 0.72rem;
        color: #555;
        cursor: pointer;
        transition: all 0.2s;

        &:hover:not(.dots) {
          border-color: #1c1c1c;
          color: #1c1c1c;
        }

        &.active {
          background: #1c1c1c;
          border-color: #1c1c1c;
          color: #f5f2ed;
        }

        &.dots {
          border: none;
          background: transparent;
          cursor: default;
          color: #b0a898;
        }
      }
    }
  }

  /* ── NO RESULTS ── */
  .no-results {
    padding: 5rem 2rem;
    text-align: center;
    border-top: 1px solid #d5d0c8;

    .no-results-tag {
      display: block;
      font-family: "Courier New", monospace;
      font-size: 0.68rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: #b0a898;
      margin-bottom: 1rem;
    }

    h3 {
      font-size: 1.4rem;
      font-weight: 400;
      color: #1c1c1c;
      margin: 0 0 0.6rem;
    }

    p {
      font-size: 0.9rem;
      color: #7a7265;
      font-style: italic;
      margin: 0 0 2rem;
    }

    .reset-btn {
      background: transparent;
      border: 1px solid #1c1c1c;
      color: #1c1c1c;
      padding: 0.7rem 2rem;
      font-family: "Courier New", monospace;
      font-size: 0.72rem;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: #1c1c1c;
        color: #f5f2ed;
      }
    }
  }

  /* ── RESPONSIVE ── */
  @media screen and (max-width: 1200px) {
    .container {
      padding: 0 3rem;
    }
  }

  @media screen and (max-width: 900px) {
    .container {
      padding: 0 2rem;
    }

    .filter-bar {
      .desktop-filters {
        display: none;
      }
      .mobile-filter-toggle {
        display: block;
      }
    }

    .product-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1.2rem;
    }
  }

  @media screen and (max-width: 600px) {
    padding: 3rem 0 4rem;
    width: 100%;
    .container {
      padding: 0 1rem;
    }

    .filter-bar .filter-top-row {
      flex-direction: column;

      .sort-select {
        width: 100%;
        min-width: unset;
      }
    }

    .product-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
    }

    .pagination {
      flex-wrap: wrap;
      gap: 0.75rem;

      .page-numbers {
        order: 3;
        width: 100%;
        justify-content: center;
      }
    }
  }

  @media screen and (max-width: 420px) {
    .container {
      padding: 0 1rem;
    }
    .product-grid {
      gap: 0.8rem;
    }
  }

  @media screen and (max-width: 350px) {
    .container {
      padding: 0 0.8rem;
    }
    .product-grid {
      gap: 0.6rem;
    }
  }
`;

export default NewProducts;